import { Controller } from "@hotwired/stimulus";

// Paired native <input type="range"> with paired <input type="number">.
// Enforces min ≤ max. Writes --low / --high CSS variables to .filter-range__sliders
// so the SCSS gradient track renders the selected range. Toggles .is-default
// when range is the full default (0–100) so the track stays neutral grey.
//
// Markup:
//   <div data-controller="filter-range" data-filter-range-min-name-value="quality_min"
//                                       data-filter-range-max-name-value="quality_max">
//     <div class="filter-range__sliders" data-filter-range-target="sliders">
//       <div class="filter-range__track"></div>
//       <input type="range" min="0" max="100" name="quality_min" data-filter-range-target="rangeMin">
//       <input type="range" min="0" max="100" name="quality_max" data-filter-range-target="rangeMax">
//     </div>
//     <div class="filter-range__inputs">
//       <input type="number" min="0" max="100" data-filter-range-target="numberMin" aria-label="Min quality">
//       <input type="number" min="0" max="100" data-filter-range-target="numberMax" aria-label="Max quality">
//     </div>
//   </div>
export default class extends Controller {
  static targets = ["sliders", "rangeMin", "rangeMax", "numberMin", "numberMax"];
  static values = {
    absoluteMin: { type: Number, default: 0 },
    absoluteMax: { type: Number, default: 100 },
    minName:     { type: String, default: "" },
    maxName:     { type: String, default: "" },
  };

  connect() {
    this._syncFromRanges();

    // HIGH regression — restore the range on Reset / preset-load / reloadDraft.
    // filter_diff dispatches "filter-diff:values-applied" with the FULL flat
    // hash and SKIPS range inputs (so it never snaps the slider to its midpoint).
    // This controller owns restoration: read detail.values[minName] / [maxName]
    // (present non-blank ⇒ use it; absent/blank ⇒ fall back to absoluteMin /
    // absoluteMax) and set the sliders + number inputs + CSS track directly.
    //
    // The event is dispatched on the DRAWER ROOT (#leads-filter-drawer, which
    // carries data-controller="filter-diff"); this controller renders INSIDE the
    // drawer body — a DESCENDANT — so a bubbling event never reaches a listener
    // on this.element (bubbling goes UP only). Bind on the ancestor that
    // actually receives it (SAME pattern as filter_pill_group / filter_chip_input).
    this._valuesAppliedHandler = (event) =>
      this._restoreFromValues(event.detail && event.detail.values);
    this._vaRoot =
      this.element.closest(".offcanvas") ||
      this.element.closest('[data-controller~="filter-diff"]') ||
      document;
    this._vaRoot.addEventListener("filter-diff:values-applied", this._valuesAppliedHandler);
  }

  disconnect() {
    if (this._valuesAppliedHandler && this._vaRoot) {
      this._vaRoot.removeEventListener("filter-diff:values-applied", this._valuesAppliedHandler);
    }
  }

  onRangeChange(event) {
    // Re-entrancy guard: locks in the invariant that this handler must not
    // recurse via dispatched events. _dispatchHiddenChange only dispatches
    // `change` (not `input`) for exactly this reason, but the guard protects
    // against someone re-adding an `input` dispatch in the future.
    if (this._syncing) return;
    this._syncing = true;
    try {
      // Enforce min ≤ max
      let lo = parseInt(this.rangeMinTarget.value, 10);
      let hi = parseInt(this.rangeMaxTarget.value, 10);
      if (lo > hi) {
        if (event.target === this.rangeMinTarget) lo = hi;
        else hi = lo;
        this.rangeMinTarget.value = lo;
        this.rangeMaxTarget.value = hi;
      }
      this.numberMinTarget.value = lo;
      this.numberMaxTarget.value = hi;
      this._updateTrack(lo, hi);
      this._dispatchHiddenChange();
    } finally {
      this._syncing = false;
    }
  }

  onNumberChange(event) {
    if (this._syncing) return;
    this._syncing = true;
    try {
      let lo = parseInt(this.numberMinTarget.value, 10);
      let hi = parseInt(this.numberMaxTarget.value, 10);
      if (isNaN(lo)) lo = this.absoluteMinValue;
      if (isNaN(hi)) hi = this.absoluteMaxValue;
      lo = Math.max(this.absoluteMinValue, Math.min(lo, this.absoluteMaxValue));
      hi = Math.max(this.absoluteMinValue, Math.min(hi, this.absoluteMaxValue));
      if (lo > hi) {
        if (event.target === this.numberMinTarget) lo = hi;
        else hi = lo;
      }
      this.rangeMinTarget.value = lo;
      this.rangeMaxTarget.value = hi;
      this.numberMinTarget.value = lo;
      this.numberMaxTarget.value = hi;
      this._updateTrack(lo, hi);
      this._dispatchHiddenChange();
    } finally {
      this._syncing = false;
    }
  }

  _updateTrack(lo, hi) {
    const span = this.absoluteMaxValue - this.absoluteMinValue;
    const loPct = ((lo - this.absoluteMinValue) / span) * 100;
    const hiPct = ((hi - this.absoluteMinValue) / span) * 100;
    this.slidersTarget.style.setProperty("--low", `${loPct}%`);
    this.slidersTarget.style.setProperty("--high", `${hiPct}%`);

    const isDefault = lo === this.absoluteMinValue && hi === this.absoluteMaxValue;
    this.element.classList.toggle("is-default", isDefault);
  }

  _dispatchHiddenChange() {
    // Only dispatch `change` — these elements listen for `input` via
    // data-action="input->filter-range#onRangeChange", so dispatching `input`
    // here causes infinite recursion (connect → _syncFromRanges → onRangeChange
    // → _dispatchHiddenChange → input dispatch → onRangeChange → …).
    // `change` has no registered action handler on these inputs and bubbles
    // up to filter-diff / filter-drawer listeners as intended.
    [this.rangeMinTarget, this.rangeMaxTarget].forEach((el) => {
      el.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  _syncFromRanges() {
    this.onRangeChange({ target: this.rangeMinTarget });
  }

  // HIGH regression — restore lo/hi from a filter-diff:values-applied payload.
  // Restoration is a PASSIVE sync (not a user edit): set rangeMin/Max +
  // numberMin/Max + the CSS track DIRECTLY under the _syncing guard, and DO NOT
  // dispatch input/change — dispatching would re-enter onRangeChange (input) or
  // re-fire the filter-diff cascade (change), defeating the whole point.
  _restoreFromValues(values) {
    if (this._syncing) return;
    const hash = values || {};
    const lo = this._coerce(hash[this.minNameValue], this.absoluteMinValue);
    const hi = this._coerce(hash[this.maxNameValue], this.absoluteMaxValue);
    // Enforce lo ≤ hi (parity with onRangeChange/onNumberChange). A hand-edited
    // or odd drilldown URL like ?quality_min=80&quality_max=20 would otherwise
    // restore an INVERTED slider (track renders backwards). Swap to preserve the
    // span rather than collapse it.
    const normLo = Math.min(lo, hi);
    const normHi = Math.max(lo, hi);

    this._syncing = true;
    try {
      this.rangeMinTarget.value = normLo;
      this.rangeMaxTarget.value = normHi;
      this.numberMinTarget.value = normLo;
      this.numberMaxTarget.value = normHi;
      this._updateTrack(normLo, normHi);
    } finally {
      this._syncing = false;
    }
  }

  // Present non-blank ⇒ clamped integer; absent / blank / NaN ⇒ fallback.
  // (fallback is the absolute bound, NOT the range-input midpoint.)
  _coerce(raw, fallback) {
    if (raw == null || String(raw).trim() === "") return fallback;
    const n = parseInt(raw, 10);
    if (isNaN(n)) return fallback;
    return Math.max(this.absoluteMinValue, Math.min(n, this.absoluteMaxValue));
  }
}
